import { makePilotInput, type InputSource, type PilotInput } from './types'

/**
 * Session record/replay — layer 3 of the desktop sim (DESIGN.md §4).
 *
 * This sidesteps controller mimicry entirely: the desktop consumes *real*
 * headset data. Replays diverge under changed physics because the human was
 * closed-loop, so compare short horizons ("same inputs, how different is the
 * first 2 s"), not whole flights. Also free crash forensics.
 */

/** 15 floats per frame: head pos+quat, then each hand pos+quat+throttle+tracked. */
const STRIDE = 3 + 4 + 2 * (3 + 4 + 1 + 1)

export interface ReplayFile {
  version: 1
  /** ms since recording start, one per frame. */
  times: number[]
  /** Flat frame data, `STRIDE` floats per frame. */
  data: number[]
  standingHeight: number
  note?: string
  recordedAt?: string
}

function push3(d: number[], v: { x: number; y: number; z: number }): void {
  d.push(v.x, v.y, v.z)
}

function push4(d: number[], q: { x: number; y: number; z: number; w: number }): void {
  d.push(q.x, q.y, q.z, q.w)
}

export class Recorder {
  private readonly times: number[] = []
  private readonly data: number[] = []
  private startTime = 0
  recording = false

  start(): void {
    this.times.length = 0
    this.data.length = 0
    this.startTime = performance.now()
    this.recording = true
  }

  stop(): void {
    this.recording = false
  }

  get frameCount(): number {
    return this.times.length
  }

  get durationSeconds(): number {
    return this.times.length ? (this.times[this.times.length - 1] - this.times[0]) / 1000 : 0
  }

  capture(input: PilotInput): void {
    if (!this.recording) return
    this.times.push(performance.now() - this.startTime)
    const d = this.data
    push3(d, input.head.position)
    push4(d, input.head.quaternion)
    for (const hand of input.hands) {
      push3(d, hand.position)
      push4(d, hand.quaternion)
      d.push(hand.throttle, hand.tracked ? 1 : 0)
    }
  }

  toFile(standingHeight: number, note?: string): ReplayFile {
    return {
      version: 1,
      times: this.times.slice(),
      // Six decimals is well under tracking noise and roughly halves the size.
      data: this.data.map((v) => Math.round(v * 1e6) / 1e6),
      standingHeight,
      note,
      recordedAt: new Date().toISOString(),
    }
  }

  download(standingHeight: number, note?: string): void {
    const blob = new Blob([JSON.stringify(this.toFile(standingHeight, note))], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `flight-${new Date().toISOString().replace(/[:.]/g, '-')}.json`
    a.click()
    URL.revokeObjectURL(url)
  }
}

export class ReplaySource implements InputSource {
  readonly name = 'replay'
  private t = 0
  private cursor = 0
  finished = false

  constructor(private readonly file: ReplayFile) {}

  get durationSeconds(): number {
    const n = this.file.times.length
    return n ? this.file.times[n - 1] / 1000 : 0
  }

  get progress(): number {
    return this.durationSeconds > 0 ? Math.min(1, this.t / this.durationSeconds) : 1
  }

  reset(): void {
    this.t = 0
    this.cursor = 0
    this.finished = false
  }

  sample(out: PilotInput, dt: number): void {
    const { times, data } = this.file
    if (times.length === 0) {
      this.finished = true
      return
    }
    const ms = this.t * 1000
    while (this.cursor < times.length - 1 && times[this.cursor + 1] <= ms) this.cursor++

    const a = this.cursor
    const b = Math.min(times.length - 1, a + 1)
    const span = times[b] - times[a]
    const alpha = span > 1e-6 ? Math.min(1, Math.max(0, (ms - times[a]) / span)) : 0

    readFrame(data, a, out)
    if (b !== a) {
      readFrame(data, b, _next)
      lerpInput(out, _next, alpha)
    }

    this.t += dt
    if (this.t >= this.durationSeconds) this.finished = true
  }
}

function readFrame(data: number[], frame: number, out: PilotInput): void {
  let o = frame * STRIDE
  out.head.position.set(data[o++], data[o++], data[o++])
  out.head.quaternion.set(data[o++], data[o++], data[o++], data[o++])
  out.head.tracked = true
  for (const hand of out.hands) {
    hand.position.set(data[o++], data[o++], data[o++])
    hand.quaternion.set(data[o++], data[o++], data[o++], data[o++])
    hand.throttle = data[o++]
    hand.tracked = data[o++] > 0.5
  }
}

function lerpInput(out: PilotInput, next: PilotInput, alpha: number): void {
  out.head.position.lerp(next.head.position, alpha)
  out.head.quaternion.slerp(next.head.quaternion, alpha)
  for (let i = 0; i < 2; i++) {
    out.hands[i].position.lerp(next.hands[i].position, alpha)
    out.hands[i].quaternion.slerp(next.hands[i].quaternion, alpha)
    out.hands[i].throttle += (next.hands[i].throttle - out.hands[i].throttle) * alpha
  }
}

const _next = makePilotInput()
